import { Injectable } from '@angular/core';
import { NotificationService } from './notification.service';
import { AuthService } from './auth.service';
import { combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class OrderWebhookService {
  constructor(
    private notificationService: NotificationService,
    private authService: AuthService
  ) {}

  /**
   * Initialize webhook listener or polling for new orders
   * This is where you'd integrate with your backend webhook endpoint
   */
  initialize(): void {
    // Only listen for notifications if user has appropriate permissions
    combineLatest([
      this.authService.isAdmin$,
      this.authService.isShopeeDocsTeam$
    ]).pipe(
      filter(([isAdmin, isShopeeDocsTeam]) => isAdmin || isShopeeDocsTeam)
    ).subscribe(([isAdmin, isShopeeDocsTeam]) => {
      if (isAdmin || isShopeeDocsTeam) {
        this.startListeningForOrders();
      }
    });
  }

  /**
   * Start listening for new orders
   * In a real implementation, this would:
   * 1. Set up WebSocket connection to your backend
   * 2. Or poll your backend API for new orders
   * 3. Or listen to Server-Sent Events (SSE)
   */
  private startListeningForOrders(): void {
    // Example WebSocket implementation:
    // const ws = new WebSocket('ws://your-backend/order-notifications');
    // ws.onmessage = (event) => {
    //   const orderData = JSON.parse(event.data);
    //   this.handleNewOrder(orderData);
    // };

    // Example polling implementation:
    // setInterval(() => {
    //   this.checkForNewOrders();
    // }, 30000); // Check every 30 seconds

    // For demo purposes, you can call this manually when Shopee sends orders:
    // this.handleNewOrder(orderData);
  }

  /**
   * Handle new order received from Shopee
   * Call this method when your backend receives a new order webhook
   */
  handleNewOrder(orderData: any): void {
    // Send to notification service (which handles buffering)
    // The permission check is already done in the initialize method
    this.notificationService.addOrderNotification(orderData);
  }

  /**
   * Example method to simulate receiving orders (for testing)
   * Remove this in production
   */
  simulateNewOrder(): void {
    const mockOrder = {
      uniqueId: `ORDER_${Date.now()}`,
      shopeeOrderNo: `SPO${Math.floor(Math.random() * 10000)}`,
      carrierTrackingNumber: `TN${Math.floor(Math.random() * 1000000)}`,
      status: 'OrderReceived',
      timestamp: new Date()
    };

    this.handleNewOrder(mockOrder);
  }

  /**
   * Example method to simulate multiple orders (for testing)
   * Remove this in production
   */
  simulateMultipleOrders(count: number = 3): void {
    for (let i = 0; i < count; i++) {
      setTimeout(() => {
        this.simulateNewOrder();
      }, i * 500); // Stagger orders by 500ms
    }
  }
}
